import { cn } from '@gto/ui/cn'

import type { ModalMediaAlign, ModalMediaWidth } from './modal-block-types'

// A share resolves against the block's own container, which inside a row is the
// column and not the card. A medium asset in a two-column row is therefore half
// of that column, which is what an operator who built the row expects to see.
const WIDTH_CLASS: Record<ModalMediaWidth, string> = {
  full: 'w-full',
  large: 'w-3/4',
  medium: 'w-1/2',
  small: 'w-1/3',
}

// Margin moves the asset, because it is a block element that has already taken
// its share of the container and only the leftover space is free.
const ALIGN_CLASS: Record<ModalMediaAlign, string> = {
  left: 'mr-auto',
  center: 'mx-auto',
  right: 'ml-auto',
}

/**
 * Picks a class for a value the API sent, falling back when the set has none.
 *
 * `hasOwn` before the index is what makes this total: the maps inherit
 * `Object.prototype`, so indexing one with `constructor` or `toString` returns a
 * function rather than nothing, and a fallback behind it would never fire.
 */
const classFor = <TKey extends string>(
  classes: Readonly<Record<TKey, string>>,
  key: string | undefined,
  fallback: string,
): string => (key !== undefined && Object.hasOwn(classes, key) ? classes[key as TKey] : fallback)

/**
 * The defaults repeat what delivery already fills in, so they never decide the
 * product behaviour. They keep a block that reached the browser without a
 * layout — a response cached before the backend deploy, or a hand-edited row —
 * rendering the way every stored media block renders today.
 *
 * A default parameter alone would not do it: it answers `undefined` and not a
 * string outside the union, which would leave the asset with no width class and
 * render it at its intrinsic size.
 */
export const modalMediaLayoutClass = (width?: ModalMediaWidth, align?: ModalMediaAlign): string =>
  cn(
    classFor(WIDTH_CLASS, width, WIDTH_CLASS.full),
    classFor(ALIGN_CLASS, align, ALIGN_CLASS.center),
  )
